import React from "react";

import { MessagesWriteError } from "types/MessagesWriteError";
import ErrorBoundary from "components/ErrorBoundary";
import MessageWriteErrorScreen from "./MessageWriteErrorScreen";

interface Props {
  children: React.ReactNode;
}

/**
 * Catches a `MessagesWriteError` — thrown during render by a component whose
 * message-write mutation has failed (`useCommitMessages` or `NewChat`) — and
 * shows a recoverable full-page crash screen (ADR 065).
 * Any other thrown value is rethrown so the app's top-level `ErrorBoundary`
 * handles it; this boundary is single-purpose. Wraps the existing
 * `ErrorBoundary`, whose render-prop `fallback` already supports the "handle
 * mine, rethrow the rest" pattern (cf. the per-thread boundary in `Chat.tsx`).
 */
export default function MessageWriteErrorBoundary({ children }: Props) {
  return (
    <ErrorBoundary
      fallback={(error, reset) => {
        if (error instanceof MessagesWriteError) {
          return (
            <MessageWriteErrorScreen fields={error.fields} reset={reset} />
          );
        }
        // Not ours — let the top-level ErrorBoundary render the generic UI.
        throw error;
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
